import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useArrangement, useAddToRepertoire, usePieceArrangements } from '@/lib/queries/pieces';
import { useMasteryLevels, useQueueStatuses, useKeys, useTimeSignatures } from '@/lib/queries/reference-data';
import { RepertoireEntryForm, type RepertoireEntryFormData } from '@/components/organisms/repertoire-entry-form';
import { Button } from '@/components/atoms/button';
import type { Arrangement } from '@/lib/types';

const GENERIC_SAVE_FAILURE = 'Please try again. If the problem persists, refresh the page.';

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  return GENERIC_SAVE_FAILURE;
}

type NewRepertoireSearch = {
  arrangementId?: string;
};

export const Route = createFileRoute('/repertoire_/new')({
  component: NewRepertoireEntryPage,
  validateSearch: (search: Record<string, unknown>): NewRepertoireSearch => ({
    arrangementId: typeof search.arrangementId === 'string' ? search.arrangementId : undefined,
  }),
});

function NewRepertoireEntryPage(): ReactElement {
  const { arrangementId } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: singleArrangement, isLoading, isError } = useArrangement(arrangementId);
  const { data: arrangementsPage } = usePieceArrangements(singleArrangement?.pieceId ?? '');
  const [selectedArrangementId, setSelectedArrangementId] = useState<string>(arrangementId ?? '');
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const { data: masteryData } = useMasteryLevels();
  const { data: queueData } = useQueueStatuses();
  const { data: keyData } = useKeys();
  const { data: timeSignatureData } = useTimeSignatures();
  const addToRepertoire = useAddToRepertoire();

  const arrangements: Arrangement[] =
    arrangementsPage?.content ?? (singleArrangement ? [singleArrangement] : []);

  const handleSave = async (data: RepertoireEntryFormData): Promise<void> => {
    setSaveErrorMessage(null);
    try {
      const entry = await addToRepertoire.mutateAsync({
        arrangementId: selectedArrangementId,
        queueStatusId: data.queueStatusId,
        masteryLevelId: data.masteryLevelId ?? undefined,
        keyId: data.keyId ?? undefined,
        practiceTempoBeats: data.practiceTempoBeats ?? undefined,
        performanceTempoBeats: data.performanceTempoBeats ?? undefined,
        notes: data.notes,
        firstPracticeOn: data.firstPracticeOn ?? null,
      });
      void queryClient.invalidateQueries({ queryKey: ['repertoire'] });
      void navigate({
        to: '/repertoire/$arrangementId',
        params: { arrangementId: entry.arrangementId },
      });
    } catch (error: unknown) {
      setSaveErrorMessage(describeError(error));
    }
  };

  const handleCancel = (): void => {
    if (singleArrangement) {
      void navigate({
        to: '/library/$pieceId',
        params: { pieceId: singleArrangement.pieceId },
      });
      return;
    }
    void navigate({ to: '/library' });
  };

  if (!arrangementId) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[var(--bg-page)] p-6 text-center">
        <p className="font-display text-xl font-semibold text-[var(--ink)]">No arrangement selected</p>
        <p className="max-w-md text-sm text-[var(--ink-3)]">
          Pick a piece in the library and add one of its arrangements to your repertoire.
        </p>
        <Link to="/library">
          <Button variant="secondary" size="sm">
            Browse library
          </Button>
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg-page)] text-sm text-[var(--ink-3)]">
        Loading…
      </div>
    );
  }

  if (isError || !singleArrangement) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[var(--bg-page)] p-6 text-center">
        <p className="font-display text-xl font-semibold text-[var(--ink)]">Arrangement not found</p>
        <Link to="/library">
          <Button variant="secondary" size="sm">
            Back to library
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pb-24 text-[var(--ink)]">
      <header className="border-b border-[var(--rule)] bg-[var(--bg-surface)] px-6 py-6 lg:px-10">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <Link
            to="/library/$pieceId"
            params={{ pieceId: singleArrangement.pieceId }}
            className="inline-flex h-10 items-center gap-2 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] px-3 text-xs font-bold text-[var(--ink)] transition-colors hover:border-[var(--brand)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10 lg:px-10">
        {saveErrorMessage !== null && (
          <div
            role="alert"
            className="mb-6 rounded-[var(--r-sm)] border border-[var(--error,red)] bg-[var(--error-wash,#fdecec)] p-4 text-sm text-[var(--error,red)]"
          >
            <p className="font-bold">Save failed</p>
            <p className="mt-1">{saveErrorMessage}</p>
          </div>
        )}
        <RepertoireEntryForm
          mode="create"
          arrangements={arrangements}
          selectedArrangementId={selectedArrangementId}
          onSelectArrangement={setSelectedArrangementId}
          masteryLevels={masteryData?.content ?? []}
          queueStatuses={queueData?.content ?? []}
          keys={keyData?.content ?? []}
          timeSignatures={timeSignatureData?.content ?? []}
          onSave={(data) => {
            void handleSave(data);
          }}
          onCancel={handleCancel}
          isSaving={addToRepertoire.isPending}
          showDangerZone={false}
          showSharedMetadata={false}
        />
      </main>
    </div>
  );
}
